import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Mic, Loader2, Sparkles, Send, RotateCcw, MessageCircle, CheckCircle2, Lightbulb, Briefcase } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const scoreTone = (s) => s >= 80 ? "text-emerald-600" : s >= 60 ? "text-sky-600" : s >= 40 ? "text-amber-600" : "text-destructive";

export default function InterviewCoach() {
    const [searchParams, setSearchParams] = useSearchParams();
    const jobContext = useMemo(() => {
        const jt = searchParams.get("role") || "";
        const co = searchParams.get("company") || "";
        const jid = searchParams.get("job_id") || "";
        const aid = searchParams.get("application_id") || "";
        if (!jt && !co && !jid && !aid) return null;
        return { job_title: jt, company: co, job_id: jid, application_id: aid };
    }, [searchParams]);

    const [roleFocus, setRoleFocus] = useState(jobContext?.job_title || "");
    const [mode, setMode] = useState("behavioral");
    const [session, setSession] = useState(null);
    const [answer, setAnswer] = useState("");
    const [loading, setLoading] = useState(false);
    const [starting, setStarting] = useState(false);
    const [history, setHistory] = useState([]);
    const endRef = useRef(null);

    const loadHistory = async () => {
        try {
            const { data } = await api.get("/professional/ai/interview/sessions");
            setHistory(data);
        } catch (err) {
            // Non-critical background load — see ResumeBuilder for rationale.
            console.warn("[InterviewCoach] loadHistory failed:", err?.message || err);
        }
    };
    useEffect(() => { loadHistory(); }, []);

    useEffect(() => {
        if (endRef.current) endRef.current.scrollIntoView({ behavior: "smooth" });
    }, [session?.turns?.length]);

    const start = async () => {
        try {
            setStarting(true);
            const body = { role_focus: roleFocus, mode };
            if (jobContext) body.job_context = jobContext;
            const { data } = await api.post("/professional/ai/interview/turn", body);
            setSession(data);
            loadHistory();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setStarting(false); }
    };

    const submitAnswer = async () => {
        if (!answer.trim() || !session) return;
        try {
            setLoading(true);
            const { data } = await api.post("/professional/ai/interview/turn", {
                session_id: session.session_id, role_focus: session.role_focus, mode: session.mode, user_message: answer,
            });
            setSession(data);
            setAnswer("");
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    };

    const resetSession = () => {
        setSession(null);
        setAnswer("");
        // Clear URL params so the next "Start" is a fresh general session unless the user re-clicks Rehearse.
        setSearchParams({});
    };

    return (
        <div data-testid="interview-coach-page">
            <div className="mb-6">
                <p className="overline">AI Career Assistant</p>
                <h1 data-testid="dash-page-title" className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">
                    Interview Coach
                </h1>
                <p className="text-sm text-muted-foreground mt-1">Rehearse behavioral + technical interviews with instant AI feedback on every answer.</p>
            </div>

            {jobContext && !session && (
                <div className="card-soft p-4 mb-4 flex items-start gap-3 border-emerald-500/30 bg-emerald-500/5" data-testid="ic-job-context">
                    <Briefcase size={18} className="text-emerald-600 mt-0.5 shrink-0" />
                    <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-400 font-bold">Candidate Readiness</div>
                        <div className="text-sm text-foreground font-semibold truncate">
                            Rehearsing for {jobContext.job_title || "this role"}{jobContext.company ? ` @ ${jobContext.company}` : ""}
                        </div>
                        <div className="text-xs text-muted-foreground">
                            The AI will tailor questions to this specific role. Change the target below if you prefer a general practice session.
                        </div>
                    </div>
                </div>
            )}

            {!session ? (
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
                    <div className="card-soft p-6">
                        <div className="text-sm text-muted-foreground mb-4">Pick a role and mode, then start.</div>
                        <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Target role</label>
                        <Input value={roleFocus} onChange={(e) => setRoleFocus(e.target.value)} placeholder="e.g. Head of Platform (leave blank to use your Passport role)" className="mt-1 mb-3" data-testid="ic-role" />
                        <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Mode</label>
                        <Select value={mode} onValueChange={setMode}>
                            <SelectTrigger className="mt-1 mb-4" data-testid="ic-mode"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="behavioral">Behavioral (STAR)</SelectItem>
                                <SelectItem value="technical">Technical</SelectItem>
                            </SelectContent>
                        </Select>
                        <button onClick={start} disabled={starting} className="btn-green w-full inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="ic-start">
                            {starting ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Start session
                        </button>
                    </div>
                    <div className="card-soft p-4">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">Recent sessions</div>
                        {history.length === 0 ? (
                            <div className="text-xs text-muted-foreground py-6 text-center">No sessions yet.</div>
                        ) : (
                            <div className="space-y-1">
                                {history.map((h) => (
                                    <button key={h.id} onClick={() => setSession({ session_id: h.id, role_focus: h.role_focus, mode: h.mode, turns: h.turns, current: h.turns[h.turns.length - 1] })}
                                        className="w-full text-left rounded-lg p-2 text-xs border border-border hover:border-emerald-500/30 transition-colors" data-testid={`ic-history-${h.id}`}>
                                        <div className="text-foreground truncate">{h.role_focus} · {h.mode}</div>
                                        <div className="text-[10px] text-muted-foreground">{h.turns.length} turn{h.turns.length === 1 ? "" : "s"} · {new Date(h.created_at).toLocaleDateString()}</div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <div>
                    <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-2">
                            <Badge className="text-[10px] border border-emerald-500/30 bg-emerald-500/10 text-emerald-600">{session.mode}</Badge>
                            <span className="text-sm text-muted-foreground">{session.role_focus}</span>
                            <span className="text-[10px] text-muted-foreground">· {session.turns.length} turn{session.turns.length === 1 ? "" : "s"}</span>
                        </div>
                        <button onClick={resetSession} className="btn-outline text-xs inline-flex items-center gap-1" data-testid="ic-reset">
                            <RotateCcw size={12} /> New session
                        </button>
                    </div>

                    <div className="space-y-4 mb-4">
                        {session.turns.map((t, i) => (
                            <motion.div key={i} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} data-testid={`ic-turn-${i}`}>
                                {t.feedback_on_prev && i > 0 && (
                                    <div className="card-soft p-4 mb-3 border-l-4 border-l-emerald-500">
                                        <div className="flex items-center justify-between mb-1">
                                            <div className="overline text-emerald-600 inline-flex items-center gap-1"><CheckCircle2 size={11} /> Feedback on your previous answer</div>
                                            <span className={`mono-score font-bold text-sm ${scoreTone(t.feedback_score)}`}>{t.feedback_score}/100</span>
                                        </div>
                                        <p className="text-sm text-foreground">{t.feedback_on_prev}</p>
                                        {(t.strengths?.length > 0 || t.improve?.length > 0) && (
                                            <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                                                {t.strengths?.length > 0 && (
                                                    <div><div className="font-semibold text-emerald-600">Strengths</div><ul className="mt-0.5 space-y-0.5">{t.strengths.map((s, j) => <li key={j} className="text-muted-foreground">• {s}</li>)}</ul></div>
                                                )}
                                                {t.improve?.length > 0 && (
                                                    <div><div className="font-semibold text-amber-600">Improve</div><ul className="mt-0.5 space-y-0.5">{t.improve.map((s, j) => <li key={j} className="text-muted-foreground">• {s}</li>)}</ul></div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                                <div className="card-soft p-5">
                                    <div className="flex items-center gap-2 mb-2">
                                        <MessageCircle size={14} className="text-emerald-500" />
                                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Question {i + 1}</span>
                                    </div>
                                    <p className="text-foreground">{t.question}</p>
                                    {t.tip && (
                                        <div className="mt-3 rounded-lg bg-amber-500/5 border border-amber-500/20 p-2 text-xs text-foreground inline-flex items-start gap-1.5">
                                            <Lightbulb size={12} className="text-amber-500 mt-0.5 shrink-0" /> {t.tip}
                                        </div>
                                    )}
                                </div>
                            </motion.div>
                        ))}
                        <div ref={endRef} />
                    </div>

                    <div className="card-soft p-4 sticky bottom-4">
                        <Textarea value={answer} onChange={(e) => setAnswer(e.target.value)} placeholder="Type your answer..." rows={4} className="mb-3" data-testid="ic-answer" />
                        <div className="flex items-center justify-between gap-2">
                            <span className="text-[11px] text-muted-foreground">Tip: use STAR (Situation, Task, Action, Result) for behavioral questions.</span>
                            <button onClick={submitAnswer} disabled={loading || !answer.trim()} className="btn-green text-sm inline-flex items-center gap-1 disabled:opacity-60" data-testid="ic-submit">
                                {loading ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Submit answer
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
